"use strict";
/**
 * ErrorSerializer - Convert errors to structured JSON for storage and logging
 * Handles: AppError, native Error, and unknown types
 * Produces: CloudWatch single-line JSON, DynamoDB documents, sanitized output
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ErrorSerializer = void 0;
const AppError_1 = require("./AppError");
const Sanitizer_1 = require("./Sanitizer");
/**
 * ErrorSerializer - Utility class for error format conversion
 */
class ErrorSerializer {
    /**
     * Convert an error to CloudWatch Logs JSON (single-line format)
     */
    static toCloudWatchLog(error, context) {
        const appError = ErrorSerializer.normalizeError(error);
        const logEntry = {
            errorId: appError.errorId,
            ownerId: context.ownerId,
            timestamp: appError.timestamp,
            severity: appError.severity,
            category: appError.category,
            message: appError.technicalMessage,
            errorType: appError.constructor.name,
            statusCode: appError.statusCode,
            lambda: context.lambdaName,
            requestId: context.requestId,
            xrayTraceId: context.xrayTraceId,
            context: appError.context,
            stack: appError.stack?.split('\n').slice(0, 5).join(' | '),
        };
        // Sanitize sensitive data
        const sanitized = Sanitizer_1.Sanitizer.sanitize(logEntry);
        // Return as single-line JSON (required for CloudWatch Insights parsing)
        return JSON.stringify(sanitized);
    }
    /**
     * Convert an error to a DynamoDB ErrorDocument
     */
    static toDynamoDBDocument(error, context) {
        const appError = ErrorSerializer.normalizeError(error);
        const doc = {
            ownerId: context.ownerId,
            timestamp: appError.timestamp,
            errorId: appError.errorId,
            category: appError.category,
            severity: appError.severity,
            errorType: appError.constructor.name,
            userMessage: appError.userMessage,
            technicalMessage: appError.technicalMessage,
            stack: appError.stack,
            context: appError.context,
            requestPath: context.requestPath,
            requestMethod: context.requestMethod,
            requestBody: context.requestBody
                ? Sanitizer_1.Sanitizer.sanitize(context.requestBody)
                : undefined,
            responseStatus: appError.statusCode,
            retryable: appError.retryable,
            lambdaName: context.lambdaName,
            requestId: context.requestId,
            xrayTraceId: context.xrayTraceId,
            sourceIp: context.sourceIp,
            sessionId: context.sessionId,
        };
        return doc;
    }
    /**
     * Normalize any error type to AppError
     * Handles: AppError (passthrough), Error instances, unknown values
     */
    static normalizeError(error) {
        if (error instanceof AppError_1.AppError) {
            return error;
        }
        if (error instanceof Error) {
            return new AppError_1.AppError({
                statusCode: 500,
                userMessage: 'Something went wrong. Please try again or contact support.',
                technicalMessage: error.message,
                category: 'SYSTEM',
                severity: 'ERROR',
                cause: error,
            });
        }
        // Unknown error type
        return new AppError_1.AppError({
            statusCode: 500,
            userMessage: 'An unexpected error occurred.',
            technicalMessage: String(error),
            category: 'SYSTEM',
            severity: 'ERROR',
        });
    }
}
exports.ErrorSerializer = ErrorSerializer;
